import {ApiFilterType} from "./ViewSettings";
import {FilterTypes} from "../../helpers/FilterTypes";
import {formatDate} from "../../helpers/DateTimeHelpers";
import {Language} from "../../config/LanguageConfig";
import {ProjectId} from "../projects/Project";
import {Identifier} from "./Identifier";

/**
 * filter names of the general data filters, as expected by the backend.
 *
 * they live in the model layer ( not in the filter input components ) so the
 * filter constructors below do not depend on the component world.
 */
export const projectFilterName = 'projectId';
export const projectStageFilterName = 'projectStageId';
export const projectStagePhaseFilterName = 'phaseId';

/**
 * Represents the type of a data filter, derived from the API filter type definition.
 */
export type DataFilterType = ApiFilterType

/**
 * Enumeration of all supported data filter type identifiers.
 * Used to classify filters by the kind of value they operate on.
 */
export const DataFilterTypes = {
    ID: 'ID',
    ID_STRING: 'ID_STRING',
    START_DATE: 'START_DATE',
    END_DATE: 'END_DATE',
    BOOL: 'BOOL',
    STRING: 'STRING'
} as const;

/**
 * Props for components that operate on a single active data filter.
 */
export interface SingleActiveDataFilterProp {
    /** The active data filter instance to work with. */
    filter: ActiveDataFilter<any>
}

/**
 * Props for components that operate on a list of active data filters.
 */
export interface ActiveDataFilterListProp {
    /** The array of active data filters to work with. */
    filters: ActiveDataFilter<any>[]
}

/**
 * Creates a human-readable display title for a filter by combining its name and current value.
 *
 * @param filterTitle - The label of the filter (e.g. "Project").
 * @param filterValueText - The textual representation of the selected filter value.
 * @returns A formatted string in the form "filterTitle: filterValueText".
 */
export const createFilterDisplayTitle = ( filterTitle: string, filterValueText: string ): string =>
    filterTitle + ": " + filterValueText;

/**
 * Determines whether the given active data filter is a date-based filter.
 *
 * @param filter - The active data filter to check.
 * @returns `true` if the filter type is END_DATE, START_DATE, or DATE.
 */
export const isDateFilter = ( filter: ActiveDataFilter<any> ): boolean =>
    ( filter.type === FilterTypes.END_DATE || filter.type === FilterTypes.START_DATE || filter.type === FilterTypes.DATE )

/** Prefix prepended to all data filter identifiers to namespace them. */
export const DATA_FILTER_PREFIX = "filter__";

/**
 * Callback signature for handling changes to an active data filter within a specific view.
 *
 * @typeParam U - The return type of the handler.
 */
export interface HandleActiveDataFilterChange<U> {
    /**
     * @typeParam T - The type of the filter's selected value.
     * @param viewIdentifier - The identifier of the view containing the filter.
     * @param filter - The active data filter that changed.
     * @returns A value of type U.
     */
    <T>( viewIdentifier: string, filter: ActiveDataFilter<T> ): U
}

/**
 * Represents a data filter that has been activated with a selected value.
 * Extends {@link DataFilter} with selection state and display metadata.
 *
 * @typeParam T - The type of the selected filter value.
 */
export interface ActiveDataFilter<T> extends DataFilter {
    /** The currently selected value for this filter. */
    selectedValue: T
    /** Whether the filter logic is inverted (i.e. exclude instead of include). */
    isInverted: boolean
    /** A human-readable name shown in the UI for this filter. */
    displayName: string
}

/**
 * Base definition of a data filter, identified by a unique string and a type classifier.
 */
export interface DataFilter {
    /** Unique identifier for the filter, prefixed with {@link DATA_FILTER_PREFIX}. */
    identifier: string
    /** The classification type of this filter. */
    type: DataFilterType
}

/**
 * Constructs a base {@link DataFilter} by combining the prefix with the given filter name.
 *
 * @param filterName - The raw name of the filter (without prefix).
 * @param type - The data filter type classifier.
 * @returns A new {@link DataFilter} instance.
 */
export const constructDataFilter = ( filterName: string, type: DataFilterType ): DataFilter => ({
    identifier: DATA_FILTER_PREFIX + filterName,
    type: type
});

/**
 * inclusive bounds of an evaluation period ( "Zeitraum" ), the base of the
 * period filter pair.
 */
export interface DataFilterPeriod {
    startAt: Date
    finishAt: Date
}

/**
 * display titles of a period filter pair: the title of the whole period and
 * the titles of its two bounds.
 */
export interface PeriodFilterTitles {
    /** title of the whole period, e.g. "Zeitraum" */
    period: string
    /** title of the start bound, e.g. "Beginn" */
    start: string
    /** title of the end bound, e.g. "Ende" */
    end: string
}

/**
 * Constructs the two active data filters that represent one evaluation period.
 *
 * the period is split into a pair of filters because the general filter
 * mechanism ( UserViewSettings / applyActiveFilters ) has no range type — a
 * period is always the pair of a date-greater-than ( start ) and a
 * date-lesser-than ( finish ) filter. both bounds carry the period title in
 * their display names, so the active filter list shows them as the two
 * bounds of one period.
 *
 * @param startFilterName - the filter name of the start bound ( e.g. "startAt" ).
 * @param endFilterName - the filter name of the end bound ( e.g. "finishAt" ).
 * @param period - the inclusive bounds of the period.
 * @param titles - the display titles of the period and its bounds.
 * @param language - language the bound dates are formatted with.
 * @param isInverted - whether the filter logic is inverted. Defaults to `false`.
 * @returns the start and end bound filters of the period, in that order.
 * @see ActiveDataFilter
 */
export function constructPeriodFiltersActive(
    startFilterName: string,
    endFilterName: string,
    period: DataFilterPeriod,
    titles: PeriodFilterTitles,
    language: Language,
    isInverted: boolean = false
): ActiveDataFilter<Date>[] {
    return [
        {
            ...constructDataFilter( startFilterName, DataFilterTypes.START_DATE ),
            isInverted: isInverted,
            selectedValue: period.startAt,
            displayName: createFilterDisplayTitle(
                titles.period + ' ' + titles.start,
                formatDate( period.startAt, language )
            )
        },
        {
            ...constructDataFilter( endFilterName, DataFilterTypes.END_DATE ),
            isInverted: isInverted,
            selectedValue: period.finishAt,
            displayName: createFilterDisplayTitle(
                titles.period + ' ' + titles.end,
                formatDate( period.finishAt, language )
            )
        }
    ];
}

/**
 * Replaces the currently active period bound filters with the given period
 * filters, keeping every other filter untouched.
 *
 * a period is identified by its bound filter identifiers, so adding a new
 * period never piles up duplicate or competing bounds in the active filter
 * list — the new period always wins as a whole.
 *
 * @param activeFilters - the currently active filters.
 * @param periodFilters - the new period bound filters ( see {@link constructPeriodFiltersActive} ).
 * @returns the active filters with the period bounds replaced.
 */
export function replacePeriodFilters(
    activeFilters: ActiveDataFilter<any>[],
    periodFilters: ActiveDataFilter<Date>[]
): ActiveDataFilter<any>[] {
    const periodFilterIdentifiers = periodFilters.map( _filter => _filter.identifier );

    return [
        ...activeFilters.filter( _filter => !periodFilterIdentifiers.includes( _filter.identifier ) ),
        ...periodFilters
    ];
}

/**
 * Constructs an {@link ActiveDataFilter} pre-configured for project-level filtering.
 *
 * @param projectId - The identifier of the project to filter by.
 * @param isInverted - Whether the filter should exclude the given project. Defaults to `false`.
 * @param displayName - A human-readable label for the filter. Defaults to `""`.
 * @returns An active data filter targeting the specified project.
 * @see ActiveDataFilter
 */
export function constructProjectFilterActive(
    projectId: Identifier<number>,
    isInverted: boolean = false,
    displayName: string = ""
): ActiveDataFilter<number> {
    return {
        ...constructDataFilter( projectFilterName, DataFilterTypes.ID ),
        isInverted: isInverted,
        displayName: displayName,
        selectedValue: projectId.rawValue
    };
}

/**
 * Constructs an {@link ActiveDataFilter} pre-configured for project-stage-level filtering.
 *
 * @param projectStageId - The identifier of the project stage to filter by.
 * @param isInverted - Whether the filter should exclude the given stage. Defaults to `false`.
 * @param displayName - A human-readable label for the filter. Defaults to `""`.
 * @returns An active data filter targeting the specified project stage.
 * @see ActiveDataFilter
 */
export function constructStageFilterActive(
    projectStageId: Identifier<number>,
    isInverted: boolean = false,
    displayName: string = ""
): ActiveDataFilter<number> {
    return {
        ...constructDataFilter( projectStageFilterName, DataFilterTypes.ID ),
        isInverted: isInverted,
        displayName: displayName,
        selectedValue: projectStageId.rawValue
    };
}

/**
 * Constructs an {@link ActiveDataFilter} pre-configured for project-stage-phase-level filtering.
 *
 * @param projectStagePhaseId - The identifier of the project stage phase to filter by.
 * @param isInverted - Whether the filter should exclude the given phase. Defaults to `false`.
 * @param displayName - A human-readable label for the filter. Defaults to `""`.
 * @returns An active data filter targeting the specified project stage phase.
 * @see ActiveDataFilter
 */
export function constructPhaseFilterActive(
    projectStagePhaseId: Identifier<number>,
    isInverted: boolean = false,
    displayName: string = ""
): ActiveDataFilter<number> {
    return {
        ...constructDataFilter( projectStagePhaseFilterName, DataFilterTypes.ID ),
        isInverted: isInverted,
        displayName: displayName,
        selectedValue: projectStagePhaseId.rawValue
    };
}